import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { LogIn, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });

      if (response.data.success) {
        login(response.data.access_token, response.data.user);
        navigate('/');
      }
    } catch (err) {
      const status = err.response?.status;
      // Server nede (503) eller ingen respons (nettverksfeil / timeout)
      if (status === 503 || status === 502 || status === 504 || !err.response) {
        setError('Innlogging feilet, serveren er nede. Vent 2-3 min og prøv igjen.');
      } else {
        setError(err.response?.data?.detail || 'Innlogging feilet');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-sm border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="text-center mb-2">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-teal-600 text-white mb-3">
              <Package size={24} />
            </div>
            <h1 className="text-xl font-semibold text-slate-900">RetailMaster</h1>
            <p className="text-sm text-slate-500 mt-0.5">Logg inn for å fortsette</p>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div>
              <Label htmlFor="email" className="text-slate-700">E-post</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din@epost.no"
                required
                autoFocus
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">Passord</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full bg-teal-600 hover:bg-teal-700"
              disabled={loading}
            >
              {loading ? 'Logger inn...' : 'Logg inn'}
            </Button>
          </form>
          <p className="text-center text-xs text-slate-400 mt-6">Holmsen Design</p>
        </CardContent>
      </Card>
    </div>
  );
}
